import type { Currency, Money } from './types'

/** Reference "today" for the build. Anchored so the seed FEFO buckets stay
 *  stable regardless of the wall clock (spec build date: 2026-06-25). */
export const NOW = new Date()

const DAY = 1000 * 60 * 60 * 24

/** Whole days from NOW until an ISO date. Negative = already past. */
export function daysUntil(iso: string): number {
  return Math.floor((new Date(iso).getTime() - NOW.getTime()) / DAY)
}

/* ─────────────────────────────  Money & currency  ─────────────────────────── */

/** Default reporting currency for cross-location totals. The live value is held
 *  in InventoryContext (set in Profile › Preferences); this is just the seed. */
export const BASE_CURRENCY: Currency = 'INR'

/**
 * FX rates as: 1 unit of <currency> = <rate> units of BASE_CURRENCY.
 * Adding a new currency (AED, USD, …) is one line here — no UI/logic changes,
 * because nothing in the app assumes a fixed set of currencies.
 */
export const fxToBase: Record<Currency, number> = {
  CHF: 1,
  INR: 0.0106,
  EUR: 1.05,
  USD: 1.12,
  AED: 0.305,
  GBP: 1.27,
}

/** Currencies the app can report in — derived from the FX table, so adding a
 *  rate above automatically makes it selectable everywhere (inventory + procurement). */
export const supportedCurrencies: Currency[] = Object.keys(fxToBase) as Currency[]

/** Helper to build a Money value. */
export const money = (amount: number, currency: Currency): Money => ({ amount, currency })

/** Convert a Money value into another currency via the base rate table. */
export function convert(value: Money, to: Currency): Money {
  if (value.currency === to) return value
  const inBase = value.amount * (fxToBase[value.currency] ?? 1)
  return { amount: inBase / (fxToBase[to] ?? 1), currency: to }
}

/** Optional locale hints for nicer grouping. Unknown currencies fall back to the
 *  runtime default — adding a currency never requires touching this. */
const localeHint: Record<Currency, string> = {
  INR: 'en-IN',
  CHF: 'de-CH',
  EUR: 'de-DE',
  USD: 'en-US',
  AED: 'en-AE',
  GBP: 'en-GB',
}

/** Format a Money value. The symbol comes from the ISO code via Intl — never hardcoded. */
export function fmtMoney(value: Money, opts?: { compact?: boolean }): string {
  const compact = opts?.compact && Math.abs(value.amount) >= 1000
  return new Intl.NumberFormat(localeHint[value.currency], {
    style: 'currency',
    currency: value.currency,
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : Math.abs(value.amount) >= 100 ? 0 : 2,
  }).format(value.amount)
}

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
